/**
 * Chart Data Helpers
 * Centralized utilities for chart data transformation and formatting
 */

import { transformToChartData, transformToChartDataAsync } from '../services/chartService';

/**
 * Create a reusable hook pattern for chart data fetching
 * This reduces code duplication across metric components
 */
export const createChartDataHook = (collectionName, valueExtractor, valueKey) => {
  return (currentData, previousData, period) => {
    if (!currentData) return null;
    
    const currentValue = currentData[valueKey] || 0;
    const previousValue = previousData?.[valueKey] || currentValue * 0.85;
    
    return transformToChartDataAsync(
      { [valueKey]: currentValue },
      { [valueKey]: previousValue },
      valueKey,
      period,
      {
        collectionName,
        valueExtractor
      }
    ).catch(() => {
      // Fallback to generated data
      return transformToChartData(
        { [valueKey]: currentValue },
        { [valueKey]: previousValue },
        valueKey,
        period
      );
    });
  };
};

/**
 * Memoized growth calculation utility
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @param {number} [decimals=1] - Number of decimal places
 * @returns {number|null} - Growth percentage or null if invalid
 */
export const calculateGrowth = (current, previous, decimals = 1) => {
  const C = Number(current || 0);
  const P = Number(previous || 0);
  
  if (!P || P === 0) {
    return C > 0 ? 100 : 0;
  }
  
  const growth = ((C - P) / P) * 100;
  return Number(growth.toFixed(decimals));
};

/**
 * Calculate growth for multiple metrics at once
 * @param {Object} current - Current period data
 * @param {Object} previous - Previous period data
 * @param {Array<string>} keys - Keys to calculate growth for
 * @returns {Object} - Growth percentages keyed by metric name
 */
export const calculateGrowthMetrics = (current, previous, keys) => {
  if (!current || !previous) return {};
  
  return keys.reduce((acc, key) => {
    const currentValue = current[key];
    const previousValue = previous[key];
    
    if (currentValue !== undefined && previousValue !== undefined) {
      acc[key] = calculateGrowth(currentValue, previousValue);
    }
    
    return acc;
  }, {});
};

/**
 * Calculate previous period value from current value and growth percentage
 * @param {number} currentValue - Current period value
 * @param {number} growthPercentage - Growth percentage (positive for increase, negative for decrease)
 * @returns {number} - Calculated previous period value
 */
export const calculatePreviousValue = (currentValue, growthPercentage) => {
  if (!growthPercentage || growthPercentage === 0) {
    // If no growth data, return 90% of current as conservative estimate
    return currentValue * 0.9;
  }
  
  // Reverse growth calculation: previous = current / (1 + growth/100)
  const growthDecimal = growthPercentage / 100;
  const previous = currentValue / (1 + growthDecimal);
  
  // Ensure we don't return negative values
  return Math.max(0, previous);
};

/**
 * Get previous period value from analytics data
 * Prefers actual previous period data, falls back to growth calculation, then to estimate
 * @param {Object} currentData - Current period analytics data
 * @param {Object} previousData - Previous period analytics data (optional)
 * @param {string} valueKey - Key to extract value from
 * @param {string} [fallbackMultiplier=0.9] - Multiplier if no data available (default 0.9 = 90%)
 * @returns {number} - Previous period value
 */
export const getPreviousPeriodValue = (currentData, previousData, valueKey, fallbackMultiplier = 0.9) => {
  if (!currentData) return 0;
  
  const currentValue = currentData[valueKey] || 0;
  
  // Priority 1: Use actual previous period data if available
  if (previousData && previousData[valueKey] !== undefined) {
    return previousData[valueKey] || 0;
  }
  
  // Priority 2: Calculate from growth percentage if available
  if (currentData.growth && currentData.growth[valueKey] !== undefined) {
    return calculatePreviousValue(currentValue, currentData.growth[valueKey]);
  }
  
  // Priority 3: Fallback to conservative estimate
  return currentValue * fallbackMultiplier;
};

/**
 * Common chart configurations
 */
export const CHART_CONFIGS = {
  splits: {
    collectionName: 'splits',
    valueExtractor: () => 1, // Count splits
    valueKey: 'totalSplits'
  },
  splitAmount: {
    collectionName: 'splits',
    valueExtractor: (docData) => parseFloat(docData.totalAmount || docData.amount || 0),
    valueKey: 'totalAmount'
  },
  transactions: {
    collectionName: 'transactions',
    valueExtractor: () => 1, // Count transactions
    valueKey: 'totalTransactionCount'
  },
  transactionVolume: {
    collectionName: 'paymentRequests',
    valueExtractor: (docData) => parseFloat(docData.amount || docData.value || 0),
    valueKey: 'totalRequestVolume'
  },
  waitlist: {
    collectionName: 'Waitlist',
    valueExtractor: () => 1, // Count subscribers
    valueKey: 'newSubscribers'
  },
  verifiedWaitlist: {
    collectionName: 'Waitlist',
    valueExtractor: (docData) => docData.emailSentStatus === 'success' ? 1 : 0,
    valueKey: 'verifiedSubscribers'
  },
  users: {
    collectionName: 'users',
    valueExtractor: () => 1, // Count users
    valueKey: 'periodUserCount'
  },
  activeUsers: {
    collectionName: 'users',
    valueExtractor: () => 1, // Count active users
    valueKey: 'activeUsers'
  },
  referrals: {
    collectionName: 'users',
    valueExtractor: (docData) => docData.referralCount || 0,
    valueKey: 'totalReferralCount'
  },
  sessions: {
    collectionName: 'users',
    valueExtractor: () => 1, // Count sessions
    valueKey: 'totalSessions'
  }
};

/**
 * Get chart config for a metric type
 * @param {string} type - Metric type
 * @returns {Object|null} - Chart configuration or null
 */
export const getChartConfig = (type) => {
  return CHART_CONFIGS[type] || null;
};

